import type { BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { ChatCircle } from "@phosphor-icons/react";
import { Badge, Flex, Text } from "@radix-ui/themes";

const DISPOSITION_COLORS: Record<string, "green" | "orange" | "gray"> = {
  resolved: "green",
  dismissed: "orange",
  default: "gray",
};

interface CommentsListProps {
  comments: BaseTicketComment[];
  activeCommentId?: string | null;
  onCommentClick?: (comment: BaseTicketComment) => void;
}

export function CommentsList({
  comments,
  activeCommentId,
  onCommentClick,
}: CommentsListProps) {
  const activeComments = comments.filter((c) => !c.isDeleted);

  if (activeComments.length === 0) return null;

  return (
    <div>
      <Flex align="center" gap="1" className="mb-2">
        <ChatCircle size={14} className="text-puntt-neutral-gray-9" />
        <Text
          size="1"
          weight="medium"
          className="text-puntt-neutral-gray-9"
        >
          Comments ({activeComments.length})
        </Text>
      </Flex>
      <div className="space-y-2">
        {activeComments.map((comment) => {
          const disposition = comment.disposition ?? "default";
          const isActive = activeCommentId === comment._id;
          return (
            <div
              key={comment._id}
              className={`rounded-md border p-3 transition-colors ${
                isActive
                  ? "border-puntt-accent-7 bg-puntt-accent-2"
                  : "border-puntt-neutral-gray-5"
              } ${onCommentClick ? "cursor-pointer hover:border-puntt-accent-6 hover:bg-puntt-neutral-gray-2" : ""}`}
              role={onCommentClick ? "button" : undefined}
              tabIndex={onCommentClick ? 0 : undefined}
              onClick={
                onCommentClick ? () => onCommentClick(comment) : undefined
              }
              onKeyDown={
                onCommentClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onCommentClick(comment);
                      }
                    }
                  : undefined
              }
            >
              <Flex align="center" gap="2" className="mb-1">
                <Badge
                  size="1"
                  variant="soft"
                  color={comment.isAI ? "blue" : "gray"}
                >
                  {comment.isAI ? "AI" : "Human"}
                </Badge>
                {disposition !== "default" && (
                  <Badge
                    size="1"
                    variant="soft"
                    color={DISPOSITION_COLORS[disposition] ?? "gray"}
                  >
                    {disposition}
                  </Badge>
                )}
                {comment.rule && (
                  <Text size="1" className="text-puntt-neutral-gray-9">
                    {comment.rule}
                  </Text>
                )}
              </Flex>
              <Text size="2" className="text-puntt-neutral-gray-12">
                {comment.description}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}
